import { useState } from 'react';
import { Wallet } from 'xrpl';
import { createXummPayload, getXummPayload, type XummCreatePayloadResponse, type XummPayloadStatusResponse } from '../xummApi';
import { XummLink } from './XummLink';

type ServiceTab = 'cold-wallet' | 'signing' | 'payid' | 'xapp' | 'qr-codes' | 'business' | 'hardware';

interface ColdWalletData {
  address: string;
  seed: string;
  publicKey: string;
  privateKey: string;
  createdAt: string;
}

interface PayloadTracking {
  uuid: string;
  type: string;
  status: XummPayloadStatusResponse | null;
  createdAt: string;
}

export default function XamanServicesHub() {
  const [activeTab, setActiveTab] = useState<ServiceTab>('cold-wallet');
  
  // Cold Wallet State
  const [coldWallets, setColdWallets] = useState<ColdWalletData[]>([]);
  const [showSeed, setShowSeed] = useState<{ [key: string]: boolean }>({});
  const [walletCount, setWalletCount] = useState(1);
  
  // Signing State
  const [txJson, setTxJson] = useState('');
  const [payloadResult, setPayloadResult] = useState<XummCreatePayloadResponse | null>(null);
  const [payloadStatus, setPayloadStatus] = useState<XummPayloadStatusResponse | null>(null);
  
  // PayID State (placeholder for future implementation)
  // const [payIdAddress, setPayIdAddress] = useState('');
  // const [payIdResult, setPayIdResult] = useState<any>(null);
  
  // QR Code State
  const [qrDestination, setQrDestination] = useState('');
  const [qrAmount, setQrAmount] = useState('');
  const [qrMemo, setQrMemo] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // Business Services State
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  
  // Tracking
  const [payloads, setPayloads] = useState<PayloadTracking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ============================================
  // COLD WALLET GENERATION (OFFLINE CAPABLE)
  // ============================================
  const generateColdWallets = () => {
    try {
      setError('');
      setSuccess('');
      
      const newWallets: ColdWalletData[] = [];
      for (let i = 0; i < walletCount; i++) {
        const wallet = Wallet.generate();
        newWallets.push({
          address: wallet.address,
          seed: wallet.seed!,
          publicKey: wallet.publicKey,
          privateKey: wallet.privateKey,
          createdAt: new Date().toISOString()
        });
      }
      
      setColdWallets([...coldWallets, ...newWallets]);
      setSuccess(`✅ Generated ${walletCount} cold wallet${walletCount > 1 ? 's' : ''} successfully!`);
      
      // Auto-download backup
      downloadWalletBackup(newWallets);
    } catch (err) {
      setError(`Failed to generate wallets: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const downloadWalletBackup = (wallets: ColdWalletData[]) => {
    const backup = {
      generated_at: new Date().toISOString(),
      network: 'XRPL Mainnet',
      warning: 'NEVER SHARE THESE SEEDS. STORE IN SECURE OFFLINE LOCATION.',
      wallets: wallets.map(w => ({
        address: w.address,
        seed: w.seed,
        publicKey: w.publicKey,
        createdAt: w.createdAt
      }))
    };
    
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unykorn-cold-wallets-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const printWallet = (_wallet: ColdWalletData) => {
    // Note: Print functionality uses window.open() which is not xApp-compliant
    // This feature should be disabled in xApp context or replaced with alternative
    alert('Print feature is not available in Xaman xApp. Please use the web version at unykorn.org');
    // Early return prevents execution of print code below
  };

  // ============================================
  // XAMAN SIGNING SERVICES
  // ============================================
  const createSigningRequest = async () => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      const txData = JSON.parse(txJson);
      const result = await createXummPayload(txData, {
        options: {
          submit: true,
          return_url: { web: window.location.href }
        }
      });
      
      setPayloadResult(result);
      
      // Track payload
      const newPayload: PayloadTracking = {
        uuid: result.uuid,
        type: txData.TransactionType || 'Unknown',
        status: null,
        createdAt: new Date().toISOString()
      };
      setPayloads([newPayload, ...payloads]);
      
      setSuccess('✅ Signing request created! Open in Xaman app to sign.');
      
      // Note: Deeplink auto-open removed for xApp compliance
      // Xaman automatically handles sign requests when running in xApp context
      // In browser, user can click the deeplink below
    } catch (err) {
      setError(`Failed to create payload: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const checkPayloadStatus = async (uuid: string) => {
    try {
      setError('');
      const status = await getXummPayload(uuid);
      setPayloadStatus(status);
      
      // Update tracking
      setPayloads(payloads.map(p => 
        p.uuid === uuid ? { ...p, status } : p
      ));
      
      if (status.meta?.signed) {
        setSuccess(`✅ Transaction signed! TxID: ${status.response?.txid || 'N/A'}`);
      }
    } catch (err) {
      setError(`Failed to check status: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // ============================================
  // PAYID LOOKUP (Placeholder - Coming Soon)
  // ============================================
  // const lookupPayId = async () => {
  //   try {
  //     setError('');
  //     setSuccess('');
  //     setLoading(true);
  //     
  //     // PayID lookup via Xaman API
  //     const response = await fetch(`https://xumm.app/api/v1/platform/payload`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({
  //         txjson: {
  //           TransactionType: 'SignIn'
  //         },
  //         options: {
  //           return_url: { web: window.location.href }
  //         }
  //       })
  //     });
  //     
  //     const data = await response.json();
  //     setPayIdResult(data);
  //     setSuccess('✅ PayID lookup initiated');
  //   } catch (err) {
  //     setError(`PayID lookup failed: ${err instanceof Error ? err.message : String(err)}`);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // ============================================
  // QR CODE GENERATION
  // ============================================
  const generatePaymentQR = () => {
    try {
      setError('');
      setSuccess('');
      
      if (!qrDestination) {
        setError('Please enter a destination address');
        return;
      }
      
      // XRPL URI format
      let uri = `https://xrpl.org/send?to=${qrDestination}`;
      if (qrAmount) uri += `&amount=${qrAmount}`;
      if (qrMemo) uri += `&memo=${encodeURIComponent(qrMemo)}`;
      
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(uri)}`;
      setQrCodeUrl(qrUrl);
      setSuccess('✅ QR Code generated!');
    } catch (err) {
      setError(`QR generation failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // ============================================
  // BUSINESS SERVICES
  // ============================================
  const requestBusinessAccount = async () => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      // Create webhook notification
      const response = await fetch('/api/xumm/webhook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'business_inquiry',
          data: {
            businessName,
            businessEmail,
            timestamp: new Date().toISOString()
          }
        })
      });
      
      if (response.ok) {
        setSuccess('Business inquiry submitted. We\'ll follow up as soon as possible (typically 1–2 business days).');
        setBusinessName('');
        setBusinessEmail('');
      } else {
        throw new Error('Failed to submit inquiry');
      }
    } catch (err) {
      setError(`Failed to submit: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#e5e7eb'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(0, 0, 0, 0.92) 100%)',
        border: '1px solid rgba(255,255,255,0.12)',
        padding: '40px',
        borderRadius: '16px',
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '12px', color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 900, marginBottom: '10px' }}>
          XAMAN SERVICES
        </div>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '36px', fontWeight: 300, color: '#ffffff' }}>
          🔐 Professional XRPL Wallet Services
        </h1>
        <p style={{ margin: 0, color: '#9ca3af', fontSize: '14px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
          Powered by Xaman (formerly XUMM) • Cold storage • Hardware wallets • Transaction signing
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        {[
          { key: 'cold-wallet', label: '❄️ Cold Wallets', desc: 'Offline generation' },
          { key: 'hardware', label: '💳 Hardware Cards', desc: 'Official Tangem' },
          { key: 'signing', label: '✍️ Sign Transactions', desc: 'Via Xaman app' },
          { key: 'payid', label: '🆔 PayID', desc: 'Lookup & verify' },
          { key: 'xapp', label: '📱 xApps', desc: 'Integrations' },
          { key: 'qr-codes', label: '📸 QR Codes', desc: 'Payment requests' },
          { key: 'business', label: '🏢 Business', desc: 'Enterprise services' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as ServiceTab)}
            style={{
              flex: '1 1 140px',
              padding: '14px',
              background: activeTab === tab.key 
                ? 'rgba(255,255,255,0.08)' 
                : 'rgba(0,0,0,0.35)',
              color: activeTab === tab.key ? '#ffffff' : '#9ca3af',
              cursor: 'pointer',
              border: activeTab === tab.key 
                ? '1px solid rgba(255,255,255,0.20)' 
                : '1px solid rgba(255,255,255,0.06)',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: activeTab === tab.key ? 'bold' : 'normal',
              transition: 'all 0.2s',
              textAlign: 'left'
            }}
          >
            <div style={{ marginBottom: '4px' }}>{tab.label}</div>
            <small style={{ opacity: 0.7, fontSize: '11px' }}>{tab.desc}</small>
          </button>
        ))}
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.10)',
          border: '1px solid rgba(239, 68, 68, 0.25)',
          padding: '16px',
          borderRadius: '12px',
          marginBottom: '20px',
          color: '#fecaca',
          fontSize: '14px'
        }}>
          ⚠️ {error}
        </div>
      )}
      
      {success && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.12)',
          border: '1px solid rgba(16, 185, 129, 0.30)',
          padding: '16px',
          borderRadius: '12px',
          marginBottom: '20px',
          color: '#6ee7b7',
          fontSize: '14px'
        }}>
          {success}
        </div>
      )}

      {/* Tab Content */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(0, 0, 0, 0.92) 100%)',
        border: '1px solid rgba(255,255,255,0.12)',
        padding: '30px',
        borderRadius: '16px'
      }}>
        {/* COLD WALLET TAB */}
        {activeTab === 'cold-wallet' && (
          <div>
            <h2>❄️ Cold Wallet Generator</h2>
            <p style={{ color: '#9ca3af', marginBottom: '30px' }}>
              Generate secure offline wallets for cold storage. All generation happens client-side in your browser.
            </p>

            <div style={{
              background: 'rgba(0,0,0,0.35)',
              border: '2px solid rgba(251, 191, 36, 0.40)',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '30px'
            }}>
              <strong style={{ color: '#ffffff' }}>🔒 Security Best Practices:</strong>
              <ul style={{ marginTop: '10px', marginBottom: 0 }}>
                <li>Generate wallets on an offline/air-gapped computer for maximum security</li>
                <li>Print wallet backups immediately and store in safe locations</li>
                <li>Never screenshot or digitally copy seed phrases</li>
                <li>Verify backups before sending funds to cold wallets</li>
                <li>Consider multi-signature for high-value storage</li>
              </ul>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#e5e7eb' }}>
                Number of wallets to generate:
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={walletCount}
                onChange={(e) => setWalletCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                style={{
                  padding: '10px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px',
                  width: '200px',
                  fontSize: '16px',
                  background: 'rgba(0,0,0,0.4)',
                  color: '#ffffff'
                }}
              />
            </div>

            <button
              onClick={generateColdWallets}
              style={{
                padding: '15px 30px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              🎲 Generate Cold Wallet{walletCount > 1 ? 's' : ''}
            </button>

            {/* Generated Wallets */}
            {coldWallets.length > 0 && (
              <div style={{ marginTop: '40px' }}>
                <h3 style={{ color: '#ffffff' }}>Generated Wallets ({coldWallets.length})</h3>
                {coldWallets.map((wallet, idx) => (
                  <div
                    key={wallet.address}
                    style={{
                      background: 'rgba(0,0,0,0.35)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '15px',
                      border: '1px solid rgba(255,255,255,0.12)'
                    }}
                  >
                    <div style={{ marginBottom: '10px' }}>
                      <strong style={{ color: '#ffffff' }}>Wallet #{idx + 1}</strong>
                      <span style={{ marginLeft: '10px', color: '#9ca3af', fontSize: '14px' }}>
                        {new Date(wallet.createdAt).toLocaleString()}
                      </span>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#93c5fd', marginBottom: '5px' }}>
                        Address (Public):
                      </label>
                      <code style={{
                        display: 'block',
                        background: 'rgba(0,0,0,0.4)',
                        padding: '10px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        wordBreak: 'break-all',
                        color: '#e5e7eb',
                        border: '1px solid rgba(255,255,255,0.06)'
                      }}>
                        {wallet.address}
                      </code>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#fca5a5', marginBottom: '5px' }}>
                        Seed (Private - NEVER SHARE):
                      </label>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <code style={{
                          flex: 1,
                          display: 'block',
                          background: 'rgba(0,0,0,0.4)',
                          padding: '10px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          wordBreak: 'break-all',
                          filter: showSeed[wallet.address] ? 'none' : 'blur(8px)',
                          transition: 'filter 0.2s',
                          color: '#e5e7eb',
                          border: '1px solid rgba(255,255,255,0.06)'
                        }}>
                          {wallet.seed}
                        </code>
                        <button
                          onClick={() => setShowSeed({ ...showSeed, [wallet.address]: !showSeed[wallet.address] })}
                          style={{
                            padding: '8px 15px',
                            background: 'rgba(255,255,255,0.10)',
                            color: '#ffffff',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {showSeed[wallet.address] ? '🙈 Hide' : '👁️ Show'}
                        </button>
                      </div>
                    </div>

                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => printWallet(wallet)}
                        style={{
                          padding: '10px 20px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        🖨️ Print Paper Wallet
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(wallet.address)}
                        style={{
                          padding: '10px 20px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        📋 Copy Address
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SIGNING TAB */}
        {activeTab === 'signing' && (
          <div>
            <h2>✍️ Transaction Signing</h2>
            <p style={{ color: '#9ca3af', marginBottom: '30px' }}>
              Create signing requests that users can approve in their Xaman mobile app. Secure and user-friendly.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#e5e7eb' }}>
                Transaction JSON:
              </label>
              <textarea
                value={txJson}
                onChange={(e) => setTxJson(e.target.value)}
                placeholder={`{
  "TransactionType": "Payment",
  "Account": "rYourAddress...",
  "Destination": "rDestination...",
  "Amount": "1000000"
}`}
                style={{
                  width: '100%',
                  minHeight: '200px',
                  padding: '15px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  background: 'rgba(0,0,0,0.4)',
                  color: '#e5e7eb'
                }}
              />
            </div>

            <button
              onClick={createSigningRequest}
              disabled={loading || !txJson.trim()}
              style={{
                padding: '15px 30px',
                background: loading || !txJson.trim() ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading || !txJson.trim() ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '⏳ Creating...' : '🚀 Create Signing Request'}
            </button>

            {payloadResult && (
              <div style={{
                marginTop: '30px',
                background: 'rgba(0,0,0,0.35)',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid rgba(110, 231, 183, 0.40)'
              }}>
                <h3 style={{ marginTop: 0, color: '#ffffff' }}>✅ Signing Request Created!</h3>
                <div style={{ marginBottom: '15px' }}>
                  <strong>UUID:</strong> <code>{payloadResult.uuid}</code>
                </div>
                {payloadResult.next?.always && (
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Deeplink:</strong><br />
                    <XummLink href={payloadResult.next.always} style={{ color: '#7c3aed' }}>
                      {payloadResult.next.always}
                    </XummLink>
                  </div>
                )}
                {payloadResult.refs?.qr_png && (
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <img src={payloadResult.refs.qr_png} alt="QR Code" style={{ maxWidth: '300px' }} />
                    <p style={{ fontSize: '14px', color: '#9ca3af' }}>Scan with Xaman app</p>
                  </div>
                )}
                <button
                  onClick={() => checkPayloadStatus(payloadResult.uuid)}
                  style={{
                    marginTop: '15px',
                    padding: '10px 20px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  🔍 Check Status
                </button>
              </div>
            )}

            {payloadStatus && (
              <div style={{
                marginTop: '20px',
                background: 'rgba(0,0,0,0.35)',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.12)'
              }}>
                <h4 style={{ marginTop: 0, color: '#ffffff' }}>Payload Status</h4>
                <pre style={{ fontSize: '12px', overflow: 'auto', color: '#e5e7eb' }}>
                  {JSON.stringify(payloadStatus, null, 2)}
                </pre>
              </div>
            )}

            {/* Recent Payloads */}
            {payloads.length > 0 && (
              <div style={{ marginTop: '40px' }}>
                <h3 style={{ color: '#ffffff' }}>Recent Signing Requests</h3>
                {payloads.slice(0, 5).map(p => (
                  <div
                    key={p.uuid}
                    style={{
                      background: 'rgba(0,0,0,0.35)',
                      padding: '15px',
                      borderRadius: '8px',
                      marginBottom: '10px',
                      border: '1px solid rgba(255,255,255,0.12)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div><strong style={{ color: '#ffffff' }}>{p.type}</strong></div>
                        <code style={{ fontSize: '12px', color: '#93c5fd' }}>{p.uuid}</code>
                      </div>
                      <div>
                        {p.status?.meta?.signed ? (
                          <span style={{ color: '#6ee7b7', fontWeight: 'bold' }}>✅ Signed</span>
                        ) : p.status?.meta?.expired ? (
                          <span style={{ color: '#fecaca', fontWeight: 'bold' }}>❌ Expired</span>
                        ) : (
                          <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>⏳ Pending</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => checkPayloadStatus(p.uuid)}
                      style={{
                        marginTop: '10px',
                        padding: '5px 10px',
                        background: 'rgba(255,255,255,0.10)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#e5e7eb'
                      }}
                    >
                      Refresh Status
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* QR CODES TAB */}
        {activeTab === 'qr-codes' && (
          <div>
            <h2>📸 QR Code Generator</h2>
            <p style={{ color: '#9ca3af', marginBottom: '30px' }}>
              Generate payment QR codes that customers can scan with Xaman or any XRPL wallet.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#e5e7eb' }}>
                Destination Address:
              </label>
              <input
                type="text"
                value={qrDestination}
                onChange={(e) => setQrDestination(e.target.value)}
                placeholder="rYourAddress..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'rgba(0,0,0,0.4)',
                  color: '#ffffff'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#e5e7eb' }}>
                Amount (XRP):
              </label>
              <input
                type="text"
                value={qrAmount}
                onChange={(e) => setQrAmount(e.target.value)}
                placeholder="10.00"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'rgba(0,0,0,0.4)',
                  color: '#ffffff'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#e5e7eb' }}>
                Memo (Optional):
              </label>
              <input
                type="text"
                value={qrMemo}
                onChange={(e) => setQrMemo(e.target.value)}
                placeholder="Payment for..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'rgba(0,0,0,0.4)',
                  color: '#ffffff'
                }}
              />
            </div>

            <button
              onClick={generatePaymentQR}
              disabled={!qrDestination.trim()}
              style={{
                padding: '15px 30px',
                background: !qrDestination.trim() ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: !qrDestination.trim() ? 'not-allowed' : 'pointer'
              }}
            >
              🎨 Generate QR Code
            </button>

            {qrCodeUrl && (
              <div style={{
                marginTop: '30px',
                textAlign: 'center',
                background: 'rgba(0,0,0,0.35)',
                padding: '30px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.12)'
              }}>
                <img src={qrCodeUrl} alt="Payment QR Code" style={{ maxWidth: '400px', margin: '0 auto' }} />
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = qrCodeUrl;
                      a.download = `payment-qr-${Date.now()}.png`;
                      a.click();
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    💾 Download QR
                  </button>
                  <button
                    onClick={() => window.print()}
                    style={{
                      padding: '10px 20px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    🖨️ Print QR
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* BUSINESS TAB */}
        {activeTab === 'business' && (
          <div>
            <h2>🏢 Business & Enterprise Services</h2>
            <p style={{ color: '#9ca3af', marginBottom: '30px' }}>
              Professional XRPL solutions for businesses. Volume pricing, dedicated support, and custom integrations.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
              marginBottom: '40px'
            }}>
              <div style={{ background: 'rgba(0,0,0,0.35)', padding: '20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)' }}>
                <h3 style={{ marginTop: 0, color: '#ffffff' }}>💼 Corporate Wallets</h3>
                <ul style={{ paddingLeft: '20px' }}>
                  <li>Multi-signature security</li>
                  <li>Role-based access control</li>
                  <li>Audit logging</li>
                  <li>Compliance reporting</li>
                </ul>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.35)', padding: '20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)' }}>
                <h3 style={{ marginTop: 0, color: '#ffffff' }}>🔐 Custody Services</h3>
                <ul style={{ paddingLeft: '20px' }}>
                  <li>Hot/warm/cold wallet architecture</li>
                  <li>Hardware wallet integration</li>
                  <li>Insurance coverage available</li>
                  <li>24/7 monitoring</li>
                </ul>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.35)', padding: '20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)' }}>
                <h3 style={{ marginTop: 0, color: '#ffffff' }}>⚡ Payment Processing</h3>
                <ul style={{ paddingLeft: '20px' }}>
                  <li>API integration</li>
                  <li>Real-time settlement</li>
                  <li>Low transaction fees</li>
                  <li>Global reach</li>
                </ul>
              </div>
            </div>

            <div style={{
              background: 'rgba(0,0,0,0.35)',
              border: '2px solid rgba(251, 191, 36, 0.40)',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '30px'
            }}>
              <strong style={{ color: '#ffffff' }}>🎯 Enterprise Features:</strong>
              <ul style={{ marginTop: '10px', marginBottom: 0 }}>
                <li>Custom xApp development</li>
                <li>White-label wallet solutions</li>
                <li>PayID registration & management</li>
                <li>Dedicated account manager</li>
                <li>SLA options</li>
                <li>Priority support</li>
              </ul>
            </div>

            <h3 style={{ color: '#ffffff' }}>Request Business Account</h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#e5e7eb' }}>
                Business Name:
              </label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Your Company Inc."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'rgba(0,0,0,0.4)',
                  color: '#ffffff'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#e5e7eb' }}>
                Business Email:
              </label>
              <input
                type="email"
                value={businessEmail}
                onChange={(e) => setBusinessEmail(e.target.value)}
                placeholder="contact@yourcompany.com"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'rgba(0,0,0,0.4)',
                  color: '#ffffff'
                }}
              />
            </div>

            <button
              onClick={requestBusinessAccount}
              disabled={loading || !businessName.trim() || !businessEmail.trim()}
              style={{
                padding: '15px 30px',
                background: loading || !businessName.trim() || !businessEmail.trim() ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading || !businessName.trim() || !businessEmail.trim() ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '⏳ Submitting...' : '📨 Request Information'}
            </button>
          </div>
        )}

        {/* HARDWARE CARDS TAB */}
        {activeTab === 'hardware' && (
          <div>
            <h2>💳 Official Xaman Hardware Cards</h2>
            <p style={{ color: '#9ca3af', marginBottom: '30px' }}>
              Powered by Tangem • EAL6+ Security • Lifetime Warranty
            </p>

            <div style={{
              background: 'rgba(0,0,0,0.35)',
              border: '2px solid rgba(110, 231, 183, 0.40)',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '30px'
            }}>
              <strong style={{ color: '#ffffff' }}>✅ Official Xaman Partner Store</strong>
              <p style={{ marginTop: '10px', marginBottom: 0, color: '#e5e7eb' }}>
                These cards are officially licensed Xaman products powered by Tangem technology.
                All sales include direct support from Unykorn and Xaman.
              </p>
            </div>

            {/* Card Options */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px',
              marginBottom: '40px'
            }}>
              {[
                {
                  name: 'Single Card',
                  price: '$49.99',
                  priceXRP: '150 XRP',
                  features: ['1 Xaman-branded Tangem card', 'EAL6+ secure element', 'NFC tap-to-sign', 'Lifetime warranty', 'Quick start guide']
                },
                {
                  name: '2-Pack',
                  price: '$89.99',
                  priceXRP: '270 XRP',
                  popular: true,
                  features: ['2 Xaman-branded cards', 'Backup card included', 'EAL6+ secure element', 'NFC tap-to-sign', 'Lifetime warranty', 'Priority support']
                },
                {
                  name: '3-Pack',
                  price: '$129.99',
                  priceXRP: '390 XRP',
                  features: ['3 Xaman-branded cards', 'Full backup redundancy', 'EAL6+ secure element', 'NFC tap-to-sign', 'Lifetime warranty', 'Premium support']
                }
              ].map((card, idx) => (
                <div
                  key={idx}
                  style={{
                    background: card.popular ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)' : 'rgba(0,0,0,0.35)',
                    border: card.popular ? '2px solid rgba(102, 126, 234, 0.60)' : '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '16px',
                    padding: '24px',
                    position: 'relative',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                >
                  {card.popular && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      right: '20px',
                      background: '#667eea',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      ⭐ POPULAR
                    </div>
                  )}
                  
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    height: '160px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '56px'
                  }}>
                    💳
                  </div>

                  <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', color: '#ffffff' }}>{card.name}</h3>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#93c5fd' }}>
                      {card.price}
                    </div>
                    <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                      or {card.priceXRP}
                    </div>
                  </div>

                  <ul style={{ paddingLeft: '20px', marginBottom: '20px', fontSize: '14px', color: '#cbd5e1' }}>
                    {card.features.map((feature, i) => (
                      <li key={i} style={{ marginBottom: '8px' }}>{feature}</li>
                    ))}
                  </ul>

                  <button
                    onClick={() => alert(`Hardware purchase - integrate with your payment processor`)}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    🛒 Add to Cart
                  </button>
                </div>
              ))}
            </div>

            {/* Why Xaman Cards */}
            <div style={{
              background: 'rgba(0,0,0,0.35)',
              padding: '30px',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.12)',
              marginBottom: '30px'
            }}>
              <h3 style={{ marginTop: 0, color: '#ffffff' }}>Why Xaman Tangem Cards?</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '20px'
              }}>
                <div>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔒</div>
                  <strong style={{ color: '#ffffff' }}>Bank-Grade Security</strong>
                  <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px' }}>
                    EAL6+ secure element chip used by governments and financial institutions.
                  </p>
                </div>
                <div>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>📱</div>
                  <strong style={{ color: '#ffffff' }}>Simple NFC Signing</strong>
                  <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px' }}>
                    Just tap your card to your phone. No cables, no hassle.
                  </p>
                </div>
                <div>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>♾️</div>
                  <strong style={{ color: '#ffffff' }}>Lifetime Warranty</strong>
                  <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px' }}>
                    Free replacement if card fails. Covers manufacturing defects forever.
                  </p>
                </div>
                <div>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>💧</div>
                  <strong style={{ color: '#ffffff' }}>Durable Design</strong>
                  <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px' }}>
                    Water resistant, no battery, works for 25+ years.
                  </p>
                </div>
              </div>
            </div>

            {/* Disclaimers */}
            <div style={{
              background: 'rgba(0,0,0,0.35)',
              border: '2px solid rgba(252, 165, 165, 0.40)',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <strong style={{ color: '#ffffff' }}>⚠️ Important Disclaimers</strong>
              <ul style={{ marginTop: '10px', paddingLeft: '20px', fontSize: '14px', color: '#e5e7eb' }}>
                <li>Cards manufactured by Tangem. Warranty claims through Tangem support.</li>
                <li>Requires Xaman mobile app (iOS/Android) to function.</li>
                <li>You are responsible for backing up your cards securely.</li>
                <li>Lost cards with no backup cannot be recovered.</li>
                <li>International orders subject to customs fees (buyer responsibility).</li>
              </ul>
            </div>
          </div>
        )}

        {/* PAYID & xAPP TABS - Placeholder */}
        {(activeTab === 'payid' || activeTab === 'xapp') && (
          <div>
            <h2>{activeTab === 'payid' ? '🆔 PayID Services' : '📱 xApp Integration'}</h2>
            <p style={{ color: '#9ca3af', marginBottom: '30px' }}>
              {activeTab === 'payid' 
                ? 'PayID services allow human-readable payment addresses. Coming soon!'
                : 'xApp integration enables deep Xaman functionality. Contact us for custom xApp development.'}
            </p>
            
            <div style={{
              background: 'rgba(0,0,0,0.35)',
              padding: '40px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>🚧</div>
              <h3 style={{ color: '#ffffff' }}>Coming Soon</h3>
              <p style={{ color: '#9ca3af' }}>This feature is currently under development. Contact us for early access.</p>
              <button
                onClick={() => setActiveTab('business')}
                style={{
                  marginTop: '20px',
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Request Enterprise Access
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '40px',
        padding: '30px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '8px',
        color: 'white',
        textAlign: 'center'
      }}>
        <h3 style={{ marginTop: 0 }}>Need Help?</h3>
        <p style={{ marginBottom: '20px' }}>
          Our team is here to assist with wallet setup, security best practices, and enterprise solutions.
        </p>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => window.location.href = 'mailto:kevanbtc@gmail.com'} 
            style={{
              padding: '12px 24px',
              background: 'white',
              color: '#7c3aed',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            📧 Email Support
          </button>
          <XummLink
            href="https://discord.gg/unykorn"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '2px solid white',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              textDecoration: 'none'
            }}
          >
            💬 Join Discord
          </XummLink>
        </div>
      </div>
    </div>
  );
}
